import { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Settings } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { URLPaths } from 'models/enums';
import SuccessDialog from 'domains/core/components/SuccessDialog';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import PageLayout from 'domains/core/components/PageLayout';
import useCreateRole from 'hooks/mutations/useCreateRole';
import useUpdateRole from 'hooks/mutations/useUpdateRole';
import useGetPermissionsList from 'hooks/queries/useGetPermissionsList';
import useGetRoleById from 'hooks/queries/useGetRoleById';
import RoleDetailsCard from '../CreateRolePage/RoleDetailsCard';
import AssignPermissionsCard from '../CreateRolePage/AssignPermissionsCard';

const PAGE_HEADER_CREATE = 'Create New Role';
const VIEW_ROLE_HEADER = 'View Role';
const EDIT_ROLE_HEADER = 'Edit Role';

const CREATE_ROLE_BUTTON_LABEL = 'Create Role';
const EDIT_ROLE_BUTTON_LABEL = 'Edit Role';
const UPDATE_ROLE_BUTTON_LABEL = 'Update Role';
const SUCCESS_DIALOG_TITLE_CREATE = 'Role created';
const SUCCESS_DIALOG_TITLE_UPDATE = 'Role updated';
const SUCCESS_DIALOG_BODY_CREATE = 'The role has been created successfully.';
const SUCCESS_DIALOG_BODY_UPDATE = 'The role has been updated successfully.';
const BACK_TO_ROLES_BUTTON_LABEL = 'Back to Roles list';

const MIN_ROLE_NAME_LENGTH = 3;

type BuilderId = 'default' | number;

const getIdFromSearch = (search: string): BuilderId | null => {
    const params = new URLSearchParams(search);
    const id = params.get('id');
    if (id == null) return null;
    if (id === 'default') return 'default';
    const num = parseInt(id, 10);
    return Number.isNaN(num) ? null : num;
};

const getModeFromSearch = (search: string): boolean => {
    const params = new URLSearchParams(search);
    return params.get('mode') === 'edit';
};

const getIsCopyFromSearch = (search: string): boolean => {
    const params = new URLSearchParams(search);
    return params.get('isCopy') === 'true';
};

const COPY_NAME_PREFIX = 'Copy of ';

const RoleBuilderPage = () => {
    const history = useHistory();
    const location = useLocation();
    const idFromQuery = useMemo(() => getIdFromSearch(location.search), [location.search]);
    const isEditMode = useMemo(() => getModeFromSearch(location.search), [location.search]);
    const isCopyFromSearch = useMemo(() => getIsCopyFromSearch(location.search), [location.search]);

    const isCreate = idFromQuery === null || idFromQuery === 'default';
    const roleId = typeof idFromQuery === 'number' ? idFromQuery : 0;
    const isCopyMode = typeof idFromQuery === 'number' && isCopyFromSearch;
    const isView = !isCreate && !isEditMode && !isCopyMode;
    const isEdit = !isCreate && isEditMode && !isCopyMode;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [permissionIds, setPermissionIds] = useState<number[]>([]);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [successIsUpdate, setSuccessIsUpdate] = useState(false);

    const { data: roleByIdData, isLoading: isRoleByIdLoading } = useGetRoleById(roleId, {
        enabled: !isCreate,
    });
    const roleFromApi =
        roleByIdData?.data?.role ??
        (roleByIdData as { role?: { name: string; description: string | null; permissions?: { id: number }[] } })?.role;

    useEffect(() => {
        if (isCreate || !roleByIdData) return;
        const role =
            roleByIdData?.data?.role ??
            (roleByIdData as { role?: { name: string; description: string | null; permissions?: { id: number }[] } })
                ?.role;
        if (!role) return;
        setName(isCopyMode ? `${COPY_NAME_PREFIX}${role.name ?? ''}` : role.name ?? '');
        setDescription(role.description ?? '');
        const ids = role.permissions?.map((p) => p.id) ?? [];
        setPermissionIds(ids);
    }, [isCreate, isCopyMode, roleByIdData]);

    const {
        mutate: mutateCreateRole,
        isLoading: isCreateRoleLoading,
        isError: isCreateRoleError,
        error: createRoleError,
    } = useCreateRole({
        onSuccess: () => {
            setSuccessIsUpdate(false);
            setIsSuccessDialogOpen(true);
        },
    });

    const updateRoleMutation = useUpdateRole(roleId, {
        onSuccess: () => {
            setSuccessIsUpdate(true);
            setIsSuccessDialogOpen(true);
        },
    });

    const { data: permissionsData, isLoading: isPermissionsLoading } = useGetPermissionsList({
        limit: 100,
        offset: 0,
    });

    const permissions = permissionsData?.data ?? [];
    const totalPermissionCount = permissionIds.length;
    const modulesConfiguredCount = new Set(permissions.filter((p) => permissionIds.includes(p.id)).map((p) => p.module))
        .size;

    const canSubmit =
        name.trim().length >= MIN_ROLE_NAME_LENGTH &&
        permissionIds.length >= 1 &&
        !isCreateRoleLoading &&
        !updateRoleMutation.isLoading;

    const handleCreateRole = useCallback(() => {
        if (!canSubmit || (!isCreate && !isCopyMode)) return;
        mutateCreateRole({
            name: name.trim(),
            description: description.trim() || undefined,
            permissionIds,
        });
    }, [canSubmit, isCreate, isCopyMode, name, description, permissionIds, mutateCreateRole]);

    const handleUpdateRole = useCallback(() => {
        if (!canSubmit || !isEdit) return;
        updateRoleMutation.mutate({
            name: name.trim(),
            description: description.trim() || undefined,
            permissionIds,
        });
    }, [canSubmit, isEdit, name, description, permissionIds, updateRoleMutation]);

    const handleEditRole = useCallback(() => {
        history.push(`${URLPaths.SETTINGS_ROLES}?id=${roleId}&mode=edit`);
    }, [history, roleId]);

    const handleBackToRolesList = useCallback(() => {
        history.push(URLPaths.SETTINGS_ROLES);
    }, [history]);

    const pageHeader =
        isView && roleFromApi
            ? `${VIEW_ROLE_HEADER}: ${roleFromApi.name}`
            : isEdit && roleFromApi
            ? `${EDIT_ROLE_HEADER}: ${roleFromApi.name}`
            : PAGE_HEADER_CREATE;
    const isPageLoading = !isCreate && isRoleByIdLoading;
    const readOnly = isView;

    const headerAction = (() => {
        if (isCreate || isCopyMode) {
            return (
                <Button
                    variant="contained"
                    onClick={handleCreateRole}
                    disabled={!canSubmit}
                    data-testid="access-center-create-role-submit-button"
                >
                    {CREATE_ROLE_BUTTON_LABEL}
                </Button>
            );
        }
        if (isView) {
            return (
                <Button variant="contained" onClick={handleEditRole} data-testid="access-center-edit-role-button">
                    {EDIT_ROLE_BUTTON_LABEL}
                </Button>
            );
        }
        if (isEdit) {
            return (
                <Button
                    variant="contained"
                    onClick={handleUpdateRole}
                    disabled={!canSubmit}
                    data-testid="access-center-update-role-submit-button"
                >
                    {UPDATE_ROLE_BUTTON_LABEL}
                </Button>
            );
        }
        return null;
    })();

    const successTitle = successIsUpdate ? SUCCESS_DIALOG_TITLE_UPDATE : SUCCESS_DIALOG_TITLE_CREATE;
    const successBody = successIsUpdate ? SUCCESS_DIALOG_BODY_UPDATE : SUCCESS_DIALOG_BODY_CREATE;

    return (
        <Stack data-testid="access-center-create-role-page" role="main" aria-label={pageHeader}>
            <PageLayout header={pageHeader} headerIcon={<Settings fontSize="inherit" />} headerAction={headerAction}>
                <Stack direction="row" spacing={3} sx={{ width: '100%' }}>
                    <Box sx={{ flex: '0 0 40%', minWidth: 0 }}>
                        <RoleDetailsCard
                            name={name}
                            description={description}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                            totalPermissionCount={totalPermissionCount}
                            modulesConfiguredCount={modulesConfiguredCount}
                            readOnly={readOnly}
                        />
                    </Box>
                    <Box sx={{ flex: '0 0 60%', minWidth: 0 }}>
                        <AssignPermissionsCard
                            permissions={permissions}
                            selectedPermissionIds={permissionIds}
                            onSelectionChange={setPermissionIds}
                            isLoading={isPermissionsLoading || isPageLoading}
                            readOnly={readOnly}
                        />
                    </Box>
                </Stack>
            </PageLayout>
            <SuccessDialog
                title={successTitle}
                body={successBody}
                isOpen={isSuccessDialogOpen}
                setIsOpen={setIsSuccessDialogOpen}
                onClose={() => setIsSuccessDialogOpen(false)}
                buttonOneText="Close"
                buttonTwoText={BACK_TO_ROLES_BUTTON_LABEL}
                buttonOneOnClick={() => {}}
                buttonTwoOnClick={handleBackToRolesList}
            />
            {isCreateRoleError && createRoleError && (
                <ErrorSnackbar
                    key={createRoleError instanceof Error ? createRoleError.message : String(createRoleError)}
                    errorMessage={createRoleError instanceof Error ? createRoleError.message : String(createRoleError)}
                />
            )}
        </Stack>
    );
};

export default RoleBuilderPage;
